import { ResizeMode } from '@wix/thumbnail-generator';
import classnames from 'classnames';
import React, { FC, memo, useCallback, useRef } from 'react';
import { DragType } from '../../../../constants/DragType';
import { stopPropagation } from '../../../../utils/dom/domEvents';
import { ActionClickOrigin } from '../../../../utils/hooks/bi/useTrackActions/useTrackActions.types';
import { TrackBoardClickTarget, useTrackBoardAnalytics } from '../../../../utils/hooks/bi/useTrackBoardAnalytics';
import { useMediaStudio } from '../../../../utils/hooks/useMediaStudio';
import { useOpenContextMenu } from '../../../../utils/hooks/useOpenContextMenu';
import { useSources } from '../../../../utils/hooks/useSources';
import { canOpenItemInImageEditor, isItemRenamable } from '../../../../utils/media-manager-sdk/items';
import { DragSource, IconButton, Input, Text } from '../../../common';
import { BoardsContextMenuTrigger } from '../../../common/BoardsContextMenu';
import { Bookmark, BookmarkFilled, CropRotateSmall, MoreSmall, RenameSmall } from '../../../common/WixIcons';
import { GenericItem } from '../../Generic/GenericItem';
import baseItemStyles from '../ListItemBase.scss';
import itemStyles from './ListFile.scss';
import { IOuter } from './ListFile.types';
import { ListFileThumbnail } from './ListFileThumbnail';

const thumbnailParams = { mode: ResizeMode.Fill };
const ListFileComponent: FC<IOuter> = ({
  item,
  className,
  selected,
  isEditing,
  isCut,
  onClick,
  onDoubleClick,
  onContextMenu,
  onRenameStart,
  onRenameCancel,
  onRenameComplete,
  onNameChange,
  openExpandedView,
  beginDrag,
  canDrag,
  selectedItems,
  currentFolder,
  onRemoveItemsFromBoard,
  source,
}) => {
  const { openMediaStudio } = useMediaStudio();
  const editFile = useCallback(
    () => openMediaStudio({ file: item, targetFolder: currentFolder }),
    [item, currentFolder],
  );

  const handleRenameStart = useCallback(
    (event) => {
      stopPropagation(event);
      onRenameStart(item.id);
    },
    [onRenameStart, item],
  );

  const canDisplayEditUnsplashAction = canOpenItemInImageEditor(item);

  const boardsContextMenuTriggerRef = useRef();
  const openBoardsContextMenu = useOpenContextMenu(boardsContextMenuTriggerRef);

  const { trackBoardClick } = useTrackBoardAnalytics();

  const handleAddToBoardClick = useCallback(
    (event) => {
      openBoardsContextMenu(event);
      trackBoardClick(item, {
        target: TrackBoardClickTarget.Add,
        clickOrigin: ActionClickOrigin.File,
      });
    },
    [openBoardsContextMenu, trackBoardClick, item],
  );

  const handleRemoveSelectedItemsFromBoard = useCallback(() => {
    const clickOrigin = ActionClickOrigin.File;

    onRemoveItemsFromBoard([item], { clickOrigin });

    trackBoardClick(item, {
      target: TrackBoardClickTarget.Remove,
      clickOrigin,
    });
  }, [item, trackBoardClick, onRemoveItemsFromBoard]);

  const { isBoardRoot } = useSources({ source });

  return (
    <GenericItem
      item={item}
      selected={selected}
      className={classnames(
        baseItemStyles.itemWrapper,
        {
          [baseItemStyles.isEditing]: isEditing,
          [baseItemStyles.selected]: selected,
          [baseItemStyles.isCut]: isCut,
        },
        className,
      )}
      itemClassName={baseItemStyles.genericItemWrapper}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
      onNameChange={onNameChange}
      onRenameComplete={onRenameComplete}
      onRenameCancel={onRenameCancel}
      dataHook="list-file"
      canDrag={canDrag}
      selectedItems={selectedItems}
    >
      {({ handleNameChange, handleRenameComplete, handleRenameCancel, openContextMenu, canDrag }) => (
        <DragSource
          className={baseItemStyles.dragSource}
          item={item}
          beginDrag={beginDrag}
          type={DragType.File}
          disabled={!canDrag || isEditing}
        >
          <div
            className={classnames(baseItemStyles.item, itemStyles.item, selected && baseItemStyles.selected, className)}
          >
            <div className={baseItemStyles.info}>
              <div className={classnames(baseItemStyles.thumbnail, itemStyles.thumbnail)}>
                <ListFileThumbnail
                  className={itemStyles.thumbnail}
                  controlsClassName={baseItemStyles.thumbnailControls}
                  labelClassName={baseItemStyles.thumbnailLabel}
                  onPlayClick={openExpandedView}
                  file={item}
                  width={40}
                  height={36}
                  params={thumbnailParams}
                />
              </div>

              <div className={baseItemStyles.nameWrapper}>
                {isEditing ? (
                  <Input
                    value={item.name}
                    onChange={handleNameChange}
                    onBlur={handleRenameComplete}
                    onEnterPressed={handleRenameComplete}
                    onEscapePressed={handleRenameCancel}
                    autoFocus
                    dataHook="list-file-name-edit-input"
                    className={baseItemStyles.editItemNameInput}
                  />
                ) : (
                  <Text
                    weight="normal"
                    size="small"
                    tagName="div"
                    className={baseItemStyles.name}
                    title={item.name}
                    dataHook="list-file-name"
                  >
                    {item.name}
                  </Text>
                )}
              </div>
            </div>

            <div className={baseItemStyles.actions}>
              {isBoardRoot ? (
                <IconButton
                  skin="light"
                  size="small"
                  className={baseItemStyles.button}
                  onClick={handleRemoveSelectedItemsFromBoard}
                  dataHook="remove-from-board-button"
                >
                  <BookmarkFilled />
                </IconButton>
              ) : (
                <BoardsContextMenuTrigger
                  contextMenuRef={boardsContextMenuTriggerRef}
                  clickOrigin={ActionClickOrigin.File}
                >
                  <IconButton
                    skin="light"
                    size="small"
                    className={baseItemStyles.button}
                    onClick={handleAddToBoardClick}
                    dataHook="add-to-board-button"
                  >
                    <Bookmark />
                  </IconButton>
                </BoardsContextMenuTrigger>
              )}

              {(canOpenItemInImageEditor(item) || canDisplayEditUnsplashAction) && (
                <IconButton
                  skin="light"
                  size="small"
                  className={baseItemStyles.button}
                  onClick={editFile}
                  dataHook="list-file-crop-edit-button"
                >
                  <CropRotateSmall />
                </IconButton>
              )}

              {isItemRenamable(item) && (
                <IconButton
                  skin="light"
                  size="small"
                  className={baseItemStyles.button}
                  onClick={handleRenameStart}
                  dataHook="list-file-rename-button"
                >
                  <RenameSmall />
                </IconButton>
              )}

              <IconButton
                skin="light"
                size="small"
                className={baseItemStyles.button}
                onClick={openContextMenu}
                dataHook="three-dots"
              >
                <MoreSmall />
              </IconButton>
            </div>
          </div>
        </DragSource>
      )}
    </GenericItem>
  );
};

export const ListFile: FC<IOuter> = memo<IOuter>(ListFileComponent);
